/* ============================================================================
   AVISOS — un solo lugar donde mirar antes de despegar.

   EL PROBLEMA QUE RESUELVE

   Hoy cada pestaña se guarda sus propias alertas: el peso excedido se ve en
   Análisis, el componente vencido en Mantenimiento, el factor de riesgo alto
   en FRAT. Para saber si el vuelo está en condiciones hay que entrar a las
   cinco. En una cabina eso no se hace así: hay un panel de anunciadores donde
   sube TODO, ordenado por gravedad, y el piloto lo mira una vez.

   Este módulo es esa lista. Es puro: entra el estado ya calculado por cada
   módulo, sale la lista ordenada. No calcula peso, ni performance, ni horas
   de componente — esos cálculos ya existen y no se duplican acá, porque dos
   implementaciones del mismo límite terminan discrepando y entonces no se
   sabe cuál creer.

   NIVELES

     alarma      rojo     límite excedido — no se despega
     precaucion  amarillo hay que revisarlo antes de despegar
     aviso       texto    hace falta saberlo, no impide nada
     normal      verde    confirmación de que algo está dentro de límites

   Se corresponden uno a uno con INTOLERABLE / TOLERABLE / ACEPTABLE, que es
   el vocabulario que ya usan la matriz de riesgo y el informe de status. No
   se inventa una escala nueva.
   ========================================================================== */

/** De más grave a menos. El orden de este arreglo ES el orden de la lista. */
export const NIVELES = ["alarma", "precaucion", "aviso", "normal"];

const PESO = Object.fromEntries(NIVELES.map((n, i) => [n, i]));

/** Traduce el vocabulario de estado de la app al nivel de aviso. */
export function nivelDesdeEstado(estado) {
  switch (estado) {
    case "INTOLERABLE":
    case "INACEPTABLE+":
    case "INACEPTABLE":
      return "alarma";
    case "TOLERABLE":
    case "GRAVE":
    case "MEDIO":
      return "precaucion";
    case "ACEPTABLE":
      return "normal";
    default:
      return "aviso";
  }
}

const av = (nivel, codigo, texto, origen, tab) => ({ nivel, codigo, texto, origen, tab });

const kg = (n) => `${Math.abs(Math.round(n))} kg`;

/**
 * Arma la lista de avisos a partir del estado ya calculado.
 *
 * Todo es opcional: si un módulo todavía no tiene datos, simplemente no
 * aporta avisos. Nunca inventa un aviso por falta de dato — un anunciador que
 * se prende porque falta información enseña a ignorarlo.
 *
 * @param {object} e
 * @param {object} [e.pesoBalance]  {pesoTotalKg, limiteKg, dentroDeEnvolvente, dentroEnAterrizaje}
 * @param {object} [e.performance]  {pesoTotalKg, limiteKg, verificadaContraRFM}
 * @param {object} [e.combustible]  {aBordoKg, requeridoKg, reservaMin}
 * @param {object} [e.mantenimiento] {filas:[{item, dato, estado}]}
 * @param {object} [e.frat]         {nivel, factoresQueExigenMedida}
 * @returns {Array<{nivel,codigo,texto,origen,tab}>} ordenados por gravedad
 */
export function construirAvisos(e = {}) {
  const fuera = [];
  const { pesoBalance: pb, performance: pf, combustible: cb, mantenimiento: mt, frat } = e;

  /* ---- Peso y balance -------------------------------------------------- */
  if (pb) {
    if (Number.isFinite(pb.pesoTotalKg) && Number.isFinite(pb.limiteKg)) {
      const exceso = pb.pesoTotalKg - pb.limiteKg;
      if (exceso > 0) {
        fuera.push(av("alarma", "PB.PESO", `Peso máximo excedido ${kg(exceso)}`, "Análisis de vuelo", "analisis"));
      } else if (exceso > -40) {
        // Menos de un pasajero de margen: no impide despegar, pero cualquier
        // cosa que se suba después lo saca de límites.
        fuera.push(av("precaucion", "PB.PESO", `Margen al peso máximo ${kg(exceso)}`, "Análisis de vuelo", "analisis"));
      }
    }
    if (pb.dentroDeEnvolvente === false) {
      fuera.push(av("alarma", "PB.CG", "CG fuera de envolvente al despegue", "Análisis de vuelo", "analisis"));
    }
    if (pb.dentroEnAterrizaje === false) {
      // Se sale de envolvente al consumir combustible: despega legal y aterriza fuera.
      fuera.push(av("alarma", "PB.CG.LDG", "CG fuera de envolvente al aterrizaje", "Análisis de vuelo", "analisis"));
    }
  }

  /* ---- Performance ----------------------------------------------------- */
  if (pf) {
    if (Number.isFinite(pf.pesoTotalKg) && Number.isFinite(pf.limiteKg)) {
      const holgura = pf.limiteKg - pf.pesoTotalKg;
      if (holgura < 0) {
        fuera.push(av("alarma", "PF.LIMITE", `Performance insuficiente por ${kg(holgura)}`, "Análisis de vuelo", "analisis"));
      } else if (holgura < 40) {
        fuera.push(av("precaucion", "PF.LIMITE", `Holgura de performance ${kg(holgura)}`, "Análisis de vuelo", "analisis"));
      }
    }
    if (pf.verificadaContraRFM === false) {
      // Decisión del proyecto: una carta importada no se da por buena hasta el
      // cotejo humano contra el RFM. El aviso lo recuerda en cada vuelo.
      fuera.push(av("aviso", "PF.SINVERIFICAR", "Cartas de performance sin cotejar contra el RFM", "Importar aeronave", "importar"));
    }
  }

  /* ---- Combustible ----------------------------------------------------- */
  if (cb && Number.isFinite(cb.aBordoKg) && Number.isFinite(cb.requeridoKg)) {
    const margen = cb.aBordoKg - cb.requeridoKg;
    if (margen < 0) {
      fuera.push(av("alarma", "CB.FALTA", `Faltan ${kg(margen)} de combustible`, "Análisis de vuelo", "analisis"));
    } else if (margen < 15) {
      fuera.push(av("precaucion", "CB.JUSTO", "Combustible sin margen sobre la reserva", "Análisis de vuelo", "analisis"));
    } else {
      fuera.push(av("normal", "CB.OK", `Combustible con ${kg(margen)} sobre lo requerido`, "Análisis de vuelo", "analisis"));
    }
  }

  /* ---- Mantenimiento --------------------------------------------------- */
  // Llegan las filas del informe de status tal cual las arma Mantenimiento.
  // Las ACEPTABLE se quedan en su pestaña: treinta líneas verdes en el panel
  // esconden la única roja.
  //
  // Se agrupa por nivel, mostrando hasta tres y resumiendo el resto. La
  // primera versión dejaba subir TODOS los vencidos, con el argumento de que
  // cada uno es un motivo distinto para no despegar. En una aeronave real con
  // doce componentes vencidos el panel se convirtió en una pared roja de la
  // que no se leía nada: el mensaje que el piloto necesita ahí es "hay doce
  // vencidos, no se despega", y el detalle de cuáles vive en Mantenimiento,
  // que es donde se resuelven. Un panel de anunciadores dice QUÉ pasa; el
  // listado completo es otra herramienta.
  const MAX_POR_NIVEL_MT = 3;
  const mostrados = { alarma: 0, precaucion: 0 };
  const resumidos = { alarma: 0, precaucion: 0 };
  for (const fila of mt?.filas ?? []) {
    const nivel = nivelDesdeEstado(fila.estado);
    if (nivel === "normal") continue;
    const cubo = nivel === "alarma" ? "alarma" : "precaucion";
    mostrados[cubo] += 1;
    if (mostrados[cubo] > MAX_POR_NIVEL_MT) { resumidos[cubo] += 1; continue; }
    fuera.push(av(nivel, `MT.${fila.item}`, `${fila.item} — ${fila.dato}`, "Mantenimiento", "mantenimiento"));
  }
  if (resumidos.alarma > 0) {
    fuera.push(av(
      "alarma", "MT.RESUMEN.VENCIDOS",
      `${resumidos.alarma} ítem(s) vencido(s) más`,
      "Mantenimiento", "mantenimiento",
    ));
  }
  if (resumidos.precaucion > 0) {
    fuera.push(av(
      "precaucion", "MT.RESUMEN",
      `${resumidos.precaucion} ítem(s) más próximos a vencer`,
      "Mantenimiento", "mantenimiento",
    ));
  }

  /* ---- FRAT ------------------------------------------------------------ */
  if (frat?.nivel) {
    const nivel = nivelDesdeEstado(frat.nivel);
    if (nivel === "alarma") {
      fuera.push(av("alarma", "FRAT.NIVEL", `Riesgo ${frat.nivel} — vuelo no autorizado`, "Riesgo (FRAT)", "frat"));
    } else if (nivel === "precaucion") {
      fuera.push(av("precaucion", "FRAT.NIVEL", `Riesgo ${frat.nivel} — requiere aprobación`, "Riesgo (FRAT)", "frat"));
    }
  }
  if (Number.isFinite(frat?.factoresQueExigenMedida) && frat.factoresQueExigenMedida > 0) {
    fuera.push(av(
      "precaucion", "FRAT.MEDIDAS",
      `${frat.factoresQueExigenMedida} factor(es) sin medida de mitigación`,
      "Riesgo (FRAT)", "frat",
    ));
  }

  // Un código, un aviso: si el mismo asunto aparece dos veces, gana el más grave.
  const porCodigo = new Map();
  for (const a of fuera) {
    const previo = porCodigo.get(a.codigo);
    if (!previo || PESO[a.nivel] < PESO[previo.nivel]) porCodigo.set(a.codigo, a);
  }

  // Orden estable: dentro del mismo nivel se respeta el orden de llegada, que
  // es el orden de las pestañas. Así la lista no baila entre renders.
  return [...porCodigo.values()].sort((a, b) => PESO[a.nivel] - PESO[b.nivel]);
}

/** Qué hace el botón máster: destella en rojo, en amarillo, o está apagado. */
export function estadoMaster(avisos = []) {
  if (avisos.some((a) => a.nivel === "alarma")) return "critico";
  if (avisos.some((a) => a.nivel === "precaucion")) return "activo";
  return "apagado";
}

/**
 * ¿Se puede despachar el vuelo?
 *
 * No es una autorización: es la lectura de si queda alguna alarma abierta. La
 * decisión sigue siendo del piloto al mando, y el aviso legal de la app lo
 * dice. Pero si hay una alarma, la app no tiene por qué disimularlo.
 */
export function despachable(avisos = []) {
  return !avisos.some((a) => a.nivel === "alarma");
}

/** Cuántos avisos hay de cada nivel, para el contador del botón máster. */
export function conteoPorNivel(avisos = []) {
  const conteo = Object.fromEntries(NIVELES.map((n) => [n, 0]));
  for (const a of avisos) conteo[a.nivel] += 1;
  return conteo;
}
